import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { colors } from '@guryou/colors';
import { useEffectOnce } from '@guryou/hooks';
import { getProviderGeneralInformation, getProviderLatestWidget, stateIsLoaded, stateIsNotInitialized } from '@guryou/redux';
import WidgetContainer from 'modules/widget';
import { readWidgetAuthBrandingFromSession } from 'constants/widgetIntegrationBranding';
import { CategoryTag, ImageWithFallback, Icon, SkeletonLoader } from '../../components/shared';

function toRgba(hex, alpha = 0.18) {
  if (!hex) return `rgba(var(--feel-primary-rgb), ${alpha})`;
  const raw = String(hex)
    .trim()
    .replace(/^#/, '');
  const normalized =
    raw.length === 3
      ? raw
          .split('')
          .map(ch => ch + ch)
          .join('')
      : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return `rgba(var(--feel-primary-rgb), ${alpha})`;
  }
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const Provider = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const params = useParams();

  const providerDetails = useSelector(state => state.generalInfo);
  const widget = useSelector(state => state.providerLatestWidget);
  const auth = useSelector(state => state.auth);
  const isLoggedIn = stateIsLoaded(auth) && !!auth?.data;
  const authBranding = React.useMemo(() => readWidgetAuthBrandingFromSession(), []);
  const authAccentColor = authBranding?.accentColor || 'var(--feel-primary)';

  const i18nCheck = t('GENERAL.actions');

  useEffectOnce(() => {
    if (['Actions', 'Azioni'].includes(i18nCheck)) {
      if (stateIsNotInitialized(providerDetails) && stateIsNotInitialized(widget)) {
        dispatch(getProviderGeneralInformation(true, +params.id));
        dispatch(getProviderLatestWidget(+params.id));
      }
    }

    //eslint-disable-next-line
  }, [i18nCheck]);

  if (!stateIsLoaded(providerDetails) || !stateIsLoaded(widget)) {
    if (isLoggedIn) {
      return (
        <section className="pt-0 pb-0" style={{ backgroundColor: colors.white }}>
          <div className="w-full px-0">
            <div className="bg-white px-6 pt-6">
              <div className="mb-4 pb-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-gray-100">
                    <div className="w-6 h-6 rounded bg-gray-200 animate-pulse" />
                  </div>
                  <div>
                    <div className="h-7 w-44 bg-gray-200 rounded animate-pulse mb-2" />
                    <div className="h-4 w-52 bg-gray-100 rounded animate-pulse" />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border-b border-gray-200 pt-6 px-4 sm:px-6 lg:px-8 pb-8">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <div className="lg:col-span-1 bg-white rounded-2xl border border-gray-200 p-5">
                  <div className="h-6 w-28 bg-gray-200 rounded animate-pulse mb-4" />
                  <div className="space-y-3">
                    {[1, 2, 3, 4].map(i => (
                      <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse" />
                    ))}
                  </div>
                </div>
                <div className="lg:col-span-3 space-y-4">
                  <div className="bg-white rounded-2xl border border-gray-200 p-6">
                    <div className="h-8 w-52 bg-gray-200 rounded animate-pulse mb-4" />
                    <div className="h-4 w-3/4 bg-gray-100 rounded animate-pulse mb-2" />
                    <div className="h-4 w-2/3 bg-gray-100 rounded animate-pulse" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[1, 2].map(i => (
                      <div key={i} className="bg-white rounded-2xl border border-gray-200 p-5">
                        <div className="h-5 w-32 bg-gray-200 rounded animate-pulse mb-3" />
                        <div className="h-4 w-full bg-gray-100 rounded animate-pulse mb-2" />
                        <div className="h-4 w-5/6 bg-gray-100 rounded animate-pulse" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      );
    }
    return <SkeletonLoader type="provider" />;
  }

  const heroImage =
    providerDetails?.data?.images?.[0]?.xLarge || providerDetails?.data?.images?.[0]?.large || providerDetails?.data?.images?.[0]?.medium;

  return (
    <section className={isLoggedIn ? 'pt-0 pb-0' : 'py-16 pt-40'} style={{ backgroundColor: colors.white }}>
      <div className={isLoggedIn ? 'w-full px-0' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'}>
        {isLoggedIn ? (
          <div className="w-full">
            <div className="bg-white px-6 pt-6">
              <div className="mb-4 pb-4 border-b border-gray-200" style={{ flexShrink: 0 }}>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="p-2 rounded-lg"
                      style={{
                        backgroundColor: toRgba(authBranding?.accentColor, 0.18),
                      }}
                    >
                      <svg className="w-6 h-6" fill="none" stroke={authAccentColor} viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900">{t('RESERVATIONS.titlePlural', 'Prenotazioni')}</h1>
                      <p className="text-sm text-gray-500 mt-0.5">{providerDetails?.data?.name || t('CALENDAR.title', 'Calendario')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white border-b border-gray-200">
              <WidgetContainer widgetId={widget?.data?.id} fullWidth />
            </div>
          </div>
        ) : (
          <>
            {/* Public view: discovery/marketing first */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 mb-10">
              <div className="relative">
                <ImageWithFallback
                  src={heroImage}
                  alt={providerDetails?.data?.name || 'Provider Image'}
                  className="w-full h-96 object-cover"
                  fallbackIcon={
                    <div className="w-full h-96 flex flex-col items-center justify-center" style={{ backgroundColor: colors.mercury }}>
                      <Icon name="building" size="2xl" color={colors.mineShaft} />
                      <p className="text-sm font-medium mt-3 opacity-75" style={{ color: colors.mineShaft }}>
                        {t('PROVIDER.noImageAvailable')}
                      </p>
                    </div>
                  }
                />
              </div>
            </div>

            {/* Provider Information */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-10">
              <div className="mb-6">
                <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: colors.mineShaft }}>
                  {providerDetails?.data?.name}
                </h1>
                <div className="flex flex-wrap gap-2 mb-6">
                  {providerDetails.data?.categories?.map((category, index) => (
                    <CategoryTag key={index} name={category.name} size="md" variant="default" />
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                {providerDetails.data?.address && (
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <Icon name="building" size="md" color={'var(--feel-primary)'} />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">{t('GENERAL.address')}</h3>
                      <p className="text-gray-900">{providerDetails.data.address}</p>
                    </div>
                  </div>
                )}
                {providerDetails.data?.email && (
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <Icon name="email" size="md" color={'var(--feel-primary)'} />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">{t('GENERAL.email')}</h3>
                      <a href={`mailto:${providerDetails.data.email}`} className="text-blue-600 hover:text-blue-800 underline transition-colors">
                        {providerDetails.data.email}
                      </a>
                    </div>
                  </div>
                )}
                {providerDetails.data?.phone && (
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <Icon name="phone" size="md" color={'var(--feel-primary)'} />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">{t('GENERAL.phone')}</h3>
                      <p className="text-gray-900">{providerDetails.data.phone}</p>
                    </div>
                  </div>
                )}
              </div>

              {providerDetails.data?.description && (
                <div className="border-t border-gray-200 pt-6">
                  <h2 className="text-xl font-semibold mb-3" style={{ color: colors.mineShaft }}>
                    {t('PROVIDER.about')}
                  </h2>
                  <p className="text-gray-600 leading-relaxed">{providerDetails.data.description}</p>
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 pt-8">
              <WidgetContainer widgetId={widget?.data?.id} />
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default Provider;
